import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  Paper,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DnsIcon from '@mui/icons-material/Dns';
import TheatersIcon from '@mui/icons-material/Theaters';
import HistoryIcon from '@mui/icons-material/History';
import HubIcon from '@mui/icons-material/Hub';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MonitorIcon from '@mui/icons-material/Monitor';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';

import { DashboardTab, MediaStoreTab, PlayKastrTab } from '../components';
import { PortalBilling } from '../components/portal/PortalBilling';
import { PortalHistory } from '../components/portal/PortalHistory';
import { PortalResources } from '../components/portal/PortalResources';
import { PortalSecurity } from '../components/portal/PortalSecurity';
import {
  ConsoleProvider,
  useRuntimeAccess,
  useRuntimeAlarms,
  useRuntimeChannel,
  useRuntimeStats,
} from '../contexts/ConsoleContext';
import { useAppTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks';
import { DestinationsView } from '../pages/console/DestinationsView';
import { setTokens } from '../utils/tokenUtils';
import { themedAlpha, tone } from '../utils/themeTokens';

const DRAWER_WIDTH = 86;

const ENTERPRISE_COLORS = {
  success: tone.success,
  error: tone.danger,
  idle: tone.disabled,
  info: tone.info,
  live: tone.success,
  offline: tone.disabled,
};

const shellCardSx = {
  borderRadius: 0,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
} as const;

type ViewKey =
  | 'PLAYKASTR'
  | 'OVERVIEW'
  | 'DESTINATIONS'
  | 'MEDIA'
  | 'RESOURCES'
  | 'BILLING'
  | 'HISTORY'
  | 'SETTINGS';

type RuntimeViewProps = {
  activeChannel: React.ComponentProps<typeof PlayKastrTab>['activeChannel'];
  user: React.ComponentProps<typeof PlayKastrTab>['user'];
};

const PlayKastrRuntimeView: React.FC<RuntimeViewProps> = ({ activeChannel, user }) => {
  const { isStreamLive, stats, analytics } = useRuntimeStats();
  return <PlayKastrTab activeChannel={activeChannel} stats={stats} analytics={analytics} isStreamLive={isStreamLive} user={user || null} />;
};

const DashboardRuntimeView: React.FC<RuntimeViewProps> = ({ activeChannel, user }) => {
  const { isStreamLive, stats, analytics } = useRuntimeStats();
  return <DashboardTab activeChannel={activeChannel} stats={stats} analytics={analytics} isStreamLive={isStreamLive} user={user || null} />;
};

const RuntimeLiveBadge: React.FC = () => {
  const { isStreamLive } = useRuntimeStats();
  const color = isStreamLive ? ENTERPRISE_COLORS.live : ENTERPRISE_COLORS.offline;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        px: 0.9,
        py: 0.4,
        borderRadius: 0,
        bgcolor: themedAlpha(color, isStreamLive ? 0.1 : 0.08),
        border: `1px solid ${color}`,
        flexShrink: 0,
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
      <Typography
        sx={{
          fontFamily: "'DM Mono', monospace",
          fontSize: '0.74rem',
          fontWeight: 800,
          color,
          textTransform: 'uppercase',
        }}
      >
        {isStreamLive ? 'Live' : 'Offline'}
      </Typography>
    </Box>
  );
};

const ConsoleContent: React.FC = () => {
  const { user, logout } = useAuth();
  const { mode, toggleColorMode } = useAppTheme();
  const logoSrc = mode === 'dark'
    ? '/streamy-logo-navy-blue-background-white-text.png'
    : '/streamy-logo-white-background-navy-blue-text.png';
  const { channels, activeChannel, setActiveChannelId } = useRuntimeChannel();
  const { isAccountDisabled } = useRuntimeAccess();
  const { audioAlertState, armAudioAlerts } = useRuntimeAlarms();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentView, setCurrentView] = useState<ViewKey>('OVERVIEW');
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState('');
  const theme = useTheme();
  const playkastrActive = !!activeChannel?.playkastrActive;
  const channelSuspended = Boolean(activeChannel?.billingSuspended);
  const channelSuspensionReason = activeChannel?.suspensionReason === 'BILLING_OVERDUE'
    ? 'Renewal payment was not received before the channel grace period ended.'
    : activeChannel?.suspensionReason === 'TRIAL_EXPIRED'
      ? 'The trial period has ended. Payment is required to activate this channel.'
    : activeChannel?.suspensionReason || 'Administrator action.';
  const trialExpired = ['TRIAL', 'PAST_DUE', 'DISABLED', 'SUSPENDED'].includes(String(user?.accountStatus || ''))
    && !user?.accountStatusBeforeRestriction
    && !user?.setupFeePaid
    && Boolean(user?.trialExpiresAt)
    && new Date(user?.trialExpiresAt || '').getTime() <= Date.now();
  const serviceRestricted = trialExpired || isAccountDisabled || user?.accountStatus === 'DISABLED';
  const operationalRestricted = serviceRestricted || channelSuspended;

  const isProfileComplete = !!(user?.firstName && user?.lastName && user?.country);

  useEffect(() => {
    const adminAccess = sessionStorage.getItem('admin_original_accessToken');
    const adminRefresh = sessionStorage.getItem('admin_original_refreshToken');
    setIsImpersonating(!!(adminAccess && adminRefresh));
    setImpersonatedUser(sessionStorage.getItem('admin_impersonation_user') || '');
  }, []);

  useEffect(() => {
    if (serviceRestricted && currentView !== 'BILLING') {
      setCurrentView('BILLING');
    }
  }, [serviceRestricted, currentView]);

  useEffect(() => {
    if (channelSuspended && !serviceRestricted && currentView !== 'BILLING') {
      setCurrentView('BILLING');
    }
  }, [channelSuspended, currentView, serviceRestricted]);

  useEffect(() => {
    if (user && !isProfileComplete && !channelSuspended && !serviceRestricted && currentView !== 'SETTINGS') {
      setCurrentView('SETTINGS');
    }
  }, [user, isProfileComplete, channelSuspended, serviceRestricted, currentView]);

  useEffect(() => {
    if (!activeChannel) return;
    if (activeChannel.playkastrActive) {
      setCurrentView((prev) => (prev === 'MEDIA' || prev === 'OVERVIEW' ? 'PLAYKASTR' : prev));
      return;
    }
    setCurrentView((prev) => (prev === 'PLAYKASTR' ? 'OVERVIEW' : prev));
  }, [activeChannel]);

  const endImpersonation = () => {
    const adminAccess = sessionStorage.getItem('admin_original_accessToken');
    const adminRefresh = sessionStorage.getItem('admin_original_refreshToken');
    if (!adminAccess || !adminRefresh) {
      logout();
      return;
    }
    setTokens(adminAccess, adminRefresh);
    sessionStorage.removeItem('admin_original_accessToken');
    sessionStorage.removeItem('admin_original_refreshToken');
    sessionStorage.removeItem('admin_impersonation_reason');
    sessionStorage.removeItem('admin_impersonation_user');
    sessionStorage.removeItem('admin_impersonation_started_at');
    window.location.href = '/admin';
  };

  const menuItems = useMemo(() => {
    if (serviceRestricted) {
      return [
        { id: 'BILLING' as ViewKey, label: 'Billing', icon: <RequestQuoteIcon /> },
      ];
    }
    const items = [];

    if (playkastrActive) {
      items.push({
        id: 'PLAYKASTR' as ViewKey,
        label: 'Rundown',
        icon: <MonitorIcon />,
        disabled: isAccountDisabled,
      });
    } else {
      items.push({
        id: 'OVERVIEW' as ViewKey,
        label: 'Overview',
        icon: <DashboardIcon />,
        disabled: isAccountDisabled,
      });
    }

    items.push(
      {
        id: 'DESTINATIONS' as ViewKey,
        label: 'Destinations',
        icon: <HubIcon />,
        disabled: isAccountDisabled,
      },
    );

    if (!playkastrActive) {
      items.push({
        id: 'MEDIA' as ViewKey,
        label: 'MediaBox',
        icon: <TheatersIcon />,
        disabled: isAccountDisabled,
      });
    }

    items.push(
      {
        id: 'RESOURCES' as ViewKey,
        label: 'Resources',
        icon: <SettingsSuggestIcon />,
        disabled: isAccountDisabled,
      },
      { id: 'BILLING' as ViewKey, label: 'Billing', icon: <RequestQuoteIcon /> },
      {
        id: 'HISTORY' as ViewKey,
        label: 'History',
        icon: <HistoryIcon />,
        disabled: isAccountDisabled,
      },
      { id: 'SETTINGS' as ViewKey, label: 'Security', icon: <SecurityIcon /> },
    );

    return items;
  }, [isAccountDisabled, playkastrActive, serviceRestricted]);

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const handleViewSelect = (view: ViewKey) => {
    setCurrentView(view);
    setMobileOpen(false);
  };

  const handleChannelSelect = (channelId: number) => {
    setActiveChannelId(channelId);
    setMobileOpen(false);
  };

  const topActionButtonSx = {
    width: 40,
    height: 40,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 0,
  };
  const audioAlertLabel = audioAlertState === 'armed'
    ? 'Alerts Armed'
    : audioAlertState === 'blocked'
      ? 'Alerts Blocked'
      : 'Arm Alerts';

  const renderView = () => {
    if (serviceRestricted && currentView !== 'BILLING') {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>
            Your {trialExpired ? 'trial' : 'subscription'} has expired. All channel services are stopped until payment is confirmed.
          </Alert>
        </Box>
      );
    }

    if (channelSuspended && !serviceRestricted && currentView !== 'BILLING') {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 0 }}>
            This channel is suspended. Console actions, inputs, MediaBox, PlayKaster and delivery outputs are disabled.
            Reason: <b>{channelSuspensionReason}</b>
          </Alert>
        </Box>
      );
    }

    return (
      <>
        {currentView === 'PLAYKASTR' && (
          <PlayKastrRuntimeView activeChannel={activeChannel} user={user} />
        )}
        {currentView === 'OVERVIEW' && (
          <DashboardRuntimeView activeChannel={activeChannel} user={user} />
        )}
        {currentView === 'DESTINATIONS' && <DestinationsView />}
        {currentView === 'BILLING' && <PortalBilling />}
        {currentView === 'RESOURCES' && <PortalResources />}
        {currentView === 'HISTORY' && <PortalHistory />}
        {currentView === 'MEDIA' && <MediaStoreTab />}
        {currentView === 'SETTINGS' && <PortalSecurity />}
      </>
    );
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <Box sx={{ p: 1 }}>
        <Paper elevation={0} sx={{ ...shellCardSx, p: 1, bgcolor: 'background.default', mb: 1.25 }}>
          <Stack spacing={1} alignItems="center">
            <Avatar
              title={`${user?.role?.replace('_', ' ') || 'User'}${isImpersonating ? ' · impersonating' : ''}`}
              sx={{
                bgcolor: isImpersonating ? 'error.main' : 'primary.main',
                width: 34,
                height: 34,
                fontWeight: 700,
                borderRadius: 0,
                fontSize: '0.9rem',
              }}
            >
              {user?.email?.charAt(0).toUpperCase()}
            </Avatar>
            {isImpersonating && (
              <Chip
                label="IMP"
                color="error"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 800,
                  borderRadius: 0,
                  maxWidth: '100%',
                }}
              />
            )}
          </Stack>
        </Paper>
      </Box>
      <Divider />
      {!serviceRestricted && (
        <Box sx={{ px: 1, py: 1.25 }}>
          <Stack spacing={0.9} alignItems="center">
          <Box
            title={`Channels (${channels.length})`}
            sx={{
              width: 44,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default',
            }}
          >
            <DnsIcon fontSize="small" />
          </Box>
          {channels.map((ch: any) => {
            const isActive = activeChannel?.id === ch.id;
            return (
              <IconButton
                key={ch.id}
                aria-label={`Switch to channel ${ch.name}`}
                title={ch.name}
                disableRipple
                disableFocusRipple
                onClick={() => handleChannelSelect(ch.id)}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 0,
                  border: '1px solid',
                  borderColor: isActive ? theme.palette.success.main : theme.palette.divider,
                  bgcolor: isActive ? alpha(theme.palette.success.main, 0.1) : 'background.paper',
                  color: isActive ? 'success.main' : 'text.primary',
                  fontWeight: 800,
                  fontSize: '0.78rem',
                  '&:hover': {
                    borderColor: isActive ? theme.palette.success.main : theme.palette.primary.main,
                    bgcolor: isActive ? alpha(theme.palette.success.main, 0.14) : alpha(theme.palette.primary.main, 0.12),
                  },
                }}
              >
                {String(ch.name || '?').charAt(0).toUpperCase()}
              </IconButton>
            );
          })}
          </Stack>
        </Box>
      )}
      {!operationalRestricted && <Divider />}
      <List sx={{ px: 1, py: 1.5, flexGrow: 1 }}>
        {menuItems.map((item) => {
          const isLockedByProfile = !isProfileComplete && item.id !== 'SETTINGS' && item.id !== 'BILLING';
          const allowedDuringBillingSuspension = item.id === 'BILLING';
          const isDisabled = !!item.disabled || isLockedByProfile || (channelSuspended && !allowedDuringBillingSuspension);
          return (
            <ListItemButton
              key={item.id}
              aria-label={item.label}
              title={item.label}
              disableRipple
              selected={currentView === item.id}
              disabled={isDisabled}
              onClick={() => !isDisabled && handleViewSelect(item.id)}
              sx={{
                borderRadius: 0,
                mb: 0.5,
                border: '1px solid',
                borderColor: currentView === item.id ? 'primary.main' : 'divider',
                bgcolor: currentView === item.id ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                '&.Mui-selected': {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  '& .MuiListItemIcon-root': { color: 'primary.main' },
                },
                opacity: isDisabled ? 0.5 : 1,
                justifyContent: 'center',
                px: 1,
                minHeight: 44,
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                {item.icon}
              </ListItemIcon>
            </ListItemButton>
          );
        })}
      </List>
      <Divider />
      <Box sx={{ p: 1 }}>
        {isImpersonating && (
          <ListItemButton
            aria-label="End Impersonation"
            title="End Impersonation"
            disableRipple
            onClick={endImpersonation}
            sx={{
              mb: 1,
              borderRadius: 0,
              color: 'error.main',
              bgcolor: alpha(theme.palette.error.main, 0.08),
              border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
              justifyContent: 'center',
              px: 1,
              minHeight: 44,
            }}
          >
            <ListItemIcon sx={{ minWidth: 0, color: 'inherit', justifyContent: 'center' }}>
              <AdminPanelSettingsIcon />
            </ListItemIcon>
          </ListItemButton>
        )}
        <ListItemButton
          aria-label="Sign Out"
          title="Sign Out"
          disableRipple
          onClick={logout}
          sx={{
            borderRadius: 0,
            color: ENTERPRISE_COLORS.error,
            bgcolor: themedAlpha(ENTERPRISE_COLORS.error, 0.05),
            border: `1px solid ${themedAlpha(ENTERPRISE_COLORS.error, 0.15)}`,
            justifyContent: 'center',
            px: 1,
            minHeight: 44,
          }}
        >
          <ListItemIcon sx={{ minWidth: 0, color: 'inherit', justifyContent: 'center' }}>
            <LogoutIcon />
          </ListItemIcon>
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box component="nav" sx={{ width: { xs: 0, md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRadius: 0,
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: `1px solid ${theme.palette.divider}`,
              borderRadius: 0,
              overflowX: 'hidden',
            },
            width: DRAWER_WIDTH,
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderBottom: `1px solid ${theme.palette.divider}`,
            borderRadius: 0,
          }}
        >
          <Toolbar sx={{ minHeight: 54, gap: 0.75 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' }, borderRadius: 0 }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              sx={{
                width: { xs: 82, sm: 112 },
                height: 30,
                px: 0.75,
                display: 'flex',
                alignItems: 'center',
                bgcolor: mode === 'dark' ? 'background.paper' : '#fff',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <Box component="img" src={logoSrc} alt="Streamy" sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
            </Box>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            {activeChannel ? (
              <Stack
                direction="row"
                spacing={0.8}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ flexGrow: 1, minWidth: 0 }}
              >
                <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: '0.74rem', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase' }}>
                  Channel Console
                </Typography>
                <RuntimeLiveBadge />
                <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: '0.74rem', fontWeight: 800, color: 'text.primary' }} noWrap>
                  {activeChannel.name}
                </Typography>
                <Chip
                  label={playkastrActive ? 'PlayKastr Mode' : 'Standard Mode'}
                  size="small"
                  sx={{
                    borderRadius: 0,
                    bgcolor: 'background.default',
                    height: 24,
                    '& .MuiChip-label': {
                      px: 1,
                      fontFamily: "'DM Mono', monospace",
                      fontSize: '0.74rem',
                      fontWeight: 800,
                    },
                  }}
                />
              </Stack>
            ) : (
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{ flexGrow: 1, minWidth: 0 }}
              >
                <Typography variant="h6" fontWeight={600} noWrap>
                  Console
                </Typography>
              </Stack>
            )}
            <Tooltip title={audioAlertState === 'armed' ? 'Alarm audio is ready' : 'Click to enable alarm audio'}>
              <Chip
                icon={audioAlertState === 'armed' ? <VolumeUpIcon /> : <VolumeOffIcon />}
                label={audioAlertLabel}
                size="small"
                variant="outlined"
                onClick={armAudioAlerts}
                color={audioAlertState === 'blocked' ? 'error' : audioAlertState === 'armed' ? 'success' : 'default'}
                sx={{
                  height: 40,
                  borderRadius: 0,
                  fontWeight: 800,
                  cursor: 'pointer',
                  '& .MuiChip-label': { display: { xs: 'none', sm: 'block' } },
                }}
              />
            </Tooltip>
            <Tooltip title={`Switch to ${mode === 'dark' ? 'light' : 'dark'} theme`}>
              <IconButton
                onClick={toggleColorMode}
                sx={{
                  ...topActionButtonSx,
                  mr: 1,
                  color: 'text.secondary',
                  bgcolor: alpha(theme.palette.action.hover, 0.25),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: 'primary.main',
                  },
                }}
              >
                {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
            </Tooltip>
            {isImpersonating && (
              <Tooltip title="End impersonation and return to Admin">
                <IconButton
                  onClick={endImpersonation}
                  sx={{
                    ...topActionButtonSx,
                    mr: 1,
                    borderColor: theme.palette.error.main,
                    color: 'error.main',
                    bgcolor: alpha(theme.palette.error.main, 0.08),
                    '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.16) },
                  }}
                >
                  <AdminPanelSettingsIcon />
                </IconButton>
              </Tooltip>
            )}
          </Toolbar>
        </AppBar>
        <Box
          component="main"
          sx={{
            p: { xs: 1, sm: 1.35, md: 1.75 },
            flexGrow: 1,
            overflowY: 'auto',
            minWidth: 0,
          }}
        >
          {channelSuspended && (
            <Alert severity="error" sx={{ mb: 1.25, borderRadius: 0 }}>
              <strong>{activeChannel?.name || 'This channel'} is suspended.</strong> {channelSuspensionReason}
            </Alert>
          )}
          {activeChannel || channels.length === 0 ? (
            renderView()
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
              }}
            >
              <CircularProgress sx={{ color: ENTERPRISE_COLORS.info }} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const ConsoleLayout: React.FC = () => (
  <ConsoleProvider>
    <ConsoleContent />
  </ConsoleProvider>
);
